import React from 'react';
import { observer } from 'mobx-react-lite';
import { ChatMessage as ChatMessageType } from '@/types/chatbot.types';
import DynamicAIResponse from './widgets/DynamicAIResponse';
import LoadingThinkingIndicator from './LoadingThinkingIndicator';
import TimeoutErrorBubble from './TimeoutErrorBubble';
import * as S from './ChatMessages.styles';

interface ChatMessageProps {
  message: ChatMessageType;
  onPublishClick?: (insightId: string) => void;
  onResendMessage?: () => void;
}

const ChatMessage: React.FC<ChatMessageProps> = observer(({ message, onPublishClick, onResendMessage }) => {
  const renderAssistantContent = () => {
    // Show timeout error if message timed out
    if (message.hasTimeoutError) {
      return <TimeoutErrorBubble onRefresh={onResendMessage ?? (() => undefined)} />;
    }

    if (message.isStreaming && !message.content && !message.widgets) {
      return <LoadingThinkingIndicator />;
    }

    const isLiveMessage = message.isStreaming === true && !message.isFromHistory;

    if (message.widgets && message.widgets.length > 0) {
      return (
        <DynamicAIResponse
          widgets={message.widgets}
          shouldAnimate={true}
          isStreaming={message.isStreaming ?? false}
          isLiveMessage={isLiveMessage}
          onPublishClick={onPublishClick}
        />
      );
    }

    if (message.content) {
      const textWidget = {
        type: 'Text' as const,
        params: {
          content: message.content,
        },
      };
      return (
        <DynamicAIResponse
          widgets={[textWidget]}
          shouldAnimate={true}
          isStreaming={message.isStreaming ?? false}
          isLiveMessage={isLiveMessage}
          onPublishClick={onPublishClick}
        />
      );
    }

    return null;
  };

  if (message.role === 'user') {
    return (
      <S.MessageWrapper role={message.role}>
        <S.MessageBubble>{message.content}</S.MessageBubble>
      </S.MessageWrapper>
    );
  }

  return <S.MessageWrapper role={message.role}>{renderAssistantContent()}</S.MessageWrapper>;
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
